import html2canvas from "html2canvas-pro";

/**
 * Rendert ein DOM-Element (typischerweise ein Graph-Widget) als JPG und
 * liefert ein Image-Objekt mit .src = Base64-Data-URI zurueck.
 *
 * Verbesserungen gegenueber der Ursprungs-Version (die auf feste 1024x350 px
 * bei JPG 0.9 gerendert hat):
 *   - Aspect-Ratio des Source-Elements wird beibehalten (keine gequetschten
 *     Graphen mehr).
 *   - html2canvas laeuft mit scale 2 (Retina-Rendering) fuer scharfe Linien.
 *   - Endgroesse wird auf max. TARGET_MAX_WIDTH heruntergerechnet, falls
 *     das gerenderte Canvas breiter ist.
 *   - Safety-Recompress: sollte die resultierende Base64-Nutzdatenmenge
 *     ueber SAFETY_THRESHOLD_BASE64_BYTES liegen (Jira-/Backend-Payload-
 *     Limits nicht garantiert bekannt), wird mit reduzierter JPG-Qualitaet
 *     und ggf. reduzierter Breite neu enkodiert.
 *   - Interface stabil: gibt weiterhin ein HTMLImageElement zurueck, dessen
 *     .src das JPG-Data-URI ist (Aufrufer nutzt result.src).
 */

// ---------------------------------------------------------------------------
// Tuning-Konstanten (bewusst am Anfang zum leichten Nachjustieren)
// ---------------------------------------------------------------------------
const CAPTURE_SCALE               = 2;         // Retina-Rendering
const TARGET_MAX_WIDTH            = 1600;      // Ziel-Breite in Pixel
const PRIMARY_JPG_QUALITY         = 0.85;      // Default JPG-Qualitaet
const SAFETY_THRESHOLD_BASE64_BYTES = 500 * 1024; // 500 KB Base64 (siehe Backend-Limit-Diskussion)

// Fallback-Stufen falls Threshold ueberschritten wird
const FALLBACK_LEVELS = [
    { maxWidth: 1600, quality: 0.70 },
    { maxWidth: 1200, quality: 0.70 },
    { maxWidth: 1024, quality: 0.65 },
];

// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------
function resizeCanvas(sourceCanvas, targetWidth) {
    if (sourceCanvas.width <= targetWidth) return sourceCanvas;
    const scale = targetWidth / sourceCanvas.width;
    const target = document.createElement('canvas');
    target.width  = targetWidth;
    target.height = Math.round(sourceCanvas.height * scale);
    const ctx = target.getContext('2d');
    // Weisser Hintergrund, damit JPG keinen schwarzen Fallback rendert
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target.width, target.height);
    ctx.drawImage(sourceCanvas, 0, 0, target.width, target.height);
    return target;
}

function estimateBase64Bytes(dataUri) {
    // data:image/jpeg;base64,<payload> — Payload ist Base64-Text; Nutzdaten
    // in Bytes ~= payload_length * 3 / 4 (- Padding). Fuer Threshold-Vergleich
    // reicht die Payload-Text-Laenge, weil sie ~1.33x der Rohbytes ist und der
    // Threshold in Base64-Bytes formuliert ist.
    const commaIdx = dataUri.indexOf(',');
    if (commaIdx < 0) return dataUri.length;
    return dataUri.length - commaIdx - 1;
}

const exportAsImage = async (element /*, imageFileName */) => {
    // 1) DOM-Element renderern (Retina-Aufloesung)
    const sourceCanvas = await html2canvas(element, {
        scale: CAPTURE_SCALE,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
    });

    // 2) Auf Zielbreite herunterrechnen (Aspect-Ratio bleibt erhalten)
    let workCanvas = resizeCanvas(sourceCanvas, TARGET_MAX_WIDTH);

    // 3) Primaer-Encode
    let dataUri = workCanvas.toDataURL('image/jpeg', PRIMARY_JPG_QUALITY);

    // 4) Safety-Recompress falls Threshold ueberschritten
    if (estimateBase64Bytes(dataUri) > SAFETY_THRESHOLD_BASE64_BYTES) {
        for (const step of FALLBACK_LEVELS) {
            const stepCanvas = resizeCanvas(sourceCanvas, step.maxWidth);
            const stepUri = stepCanvas.toDataURL('image/jpeg', step.quality);
            dataUri = stepUri;
            if (estimateBase64Bytes(stepUri) <= SAFETY_THRESHOLD_BASE64_BYTES) break;
        }
    }

    const img = new Image();
    img.src = dataUri;
    return img;
};

export default exportAsImage;
